import { AiSetupPage } from "@/pages/ai-setup";
import { AssistantPage } from "@/pages/assistant";
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { ProtectedRoute, GuestOnlyRoute } from '@/components/auth/protected-route';
import { SocketProvider } from '@/lib/socket';
import { ThemeProvider } from '@/components/ui/theme-provider';

// Pages
import { PublicProjectPage } from '@/pages/public-project';
import { HomePage } from '@/pages/home';
import { LoginPage } from '@/pages/login';
import { RegisterPage } from '@/pages/register';
import { ForgotPasswordPage } from '@/pages/forgot-password';
import { ResetPasswordPage } from '@/pages/reset-password';
import { VerifyEmailPage } from '@/pages/verify-email';
import { VerifyEmailSentPage } from '@/pages/verify-email-sent';
import { DashboardPage } from '@/pages/dashboard';
import { ProjectsPage } from '@/pages/projects';
import { ProjectDetailPage } from '@/pages/project-detail';
import { ProjectSettingsPage } from '@/pages/project-settings';
import { InvitePage } from '@/pages/invite';
import { JoinPage } from '@/pages/join';
import { InviteAcceptPage } from '@/pages/invite-accept';
import { NotificationsPage } from '@/pages/notifications';
import { TasksPage } from '@/pages/tasks';
import { SettingsPage } from '@/pages/settings';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SocketProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/public-project/:token" element={<PublicProjectPage />} />

            {/* Guest only routes (redirect to dashboard if logged in) */}
            <Route
              path="/login"
              element={
                <GuestOnlyRoute>
                  <LoginPage />
                </GuestOnlyRoute>
              }
            />
            <Route
              path="/register"
              element={
                <GuestOnlyRoute>
                  <RegisterPage />
                </GuestOnlyRoute>
              }
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* R24: public landing page for the verification e-mail link. */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            {/* R24: post-signup "check your inbox" page (refreshable). */}
            <Route path="/verify-email-sent" element={<VerifyEmailSentPage />} />

            {/* Protected routes */}
            <Route path="/assistant" element={<ProtectedRoute><AssistantPage /></ProtectedRoute>} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <ProtectedRoute>
                  <ProjectDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/settings"
              element={
                <ProtectedRoute>
                  <ProjectSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <TasksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            <Route path="/ai/setup" element={<ProtectedRoute><AiSetupPage /></ProtectedRoute>} />

            {/* Invitation page - public but shows login prompt if not authenticated */}
            <Route path="/invite/:token" element={<InvitePage />} />
            {/* R14 (t46): multi-link invite accept page (auth required). */}
            <Route path="/invite-link/:token" element={<InviteAcceptPage />} />
            <Route path="/join/:token" element={<JoinPage />} />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster />
        </SocketProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
